/**
 * TierBadge — Reusable membership tier indicator badge.
 *
 * Usage:
 *   <TierBadge tier="business" />
 *   <TierBadge tier="pro" size="xs" />
 */
import type { MembershipTier } from '@/modules/shared/hooks/useMembership'

interface TierConfig {
  label: string
  className: string
}

const TIER_META: Record<MembershipTier, TierConfig> = {
  free: {
    label: 'Free',
    className: 'bg-[var(--y-surface-tertiary)] text-[var(--y-text-tertiary)]',
  },
  starter: {
    label: 'Starter',
    className: 'bg-[var(--y-accent)]/10 text-[var(--y-accent)]',
  },
  pro: {
    label: 'Pro ✦',
    className: 'bg-[var(--y-accent)]/20 text-[var(--y-accent)] font-semibold',
  },
  business: {
    label: 'Business',
    className: 'bg-[var(--y-accent)] text-[var(--y-accent-text)] font-semibold',
  },
}

interface TierBadgeProps {
  tier: MembershipTier
  /** xs = 10px pill (inside buttons), sm = 12px pill (standalone) */
  size?: 'xs' | 'sm'
}

export function TierBadge({ tier, size = 'sm' }: TierBadgeProps) {
  const meta = TIER_META[tier]
  const sizeClass = size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'
  return (
    <span className={`inline-flex items-center rounded-full ${sizeClass} ${meta.className}`}>
      {meta.label}
    </span>
  )
}
